import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import type { SkillData } from '@sb/feathers-types';
import type {
  CreateTrainingRunJobMutation,
  CreateTrainingRunJobMutationVariables,
} from '@sbrc/gql/apollo-codegen';

const CREATE_TRAINING_RUN_JOB = gql`
  mutation CreateTrainingRunJob($input: CreateTrainingRunJobInput!) {
    createTrainingRunJob(input: $input) {
      success
      error
    }
  }
`;

// Hardcoded defaults for the one-click Train flow. A follow-up PR will move
// these to skill-level settings with an edit UI.
const DEFAULT_DOCKER_IMAGE_TAG = 'main';
const DEFAULT_PLATFORM = 'runpod';
const DEFAULT_GPU_COUNT = 1;
const DEFAULT_GPU_TYPE = 'H100';
const DEFAULT_CONTAINER_DISK_GB = 1024;
const DEFAULT_MAX_HOURS = 48;

interface SkillConfig {
  taskId?: string;
  projectId?: string;
}

function buildRunName(skillName: string): string {
  // Appends an ISO-minute timestamp so re-trains don't clobber each other in
  // the runs list. e.g. "Pick block 2026-04-15T15:30".
  return `${skillName} ${new Date().toISOString().slice(0, 16)}`;
}

export interface StartTrainingResult {
  success: boolean;
  error: string | null;
}

export function useStartSkillTraining(skill: SkillData) {
  const [mutate, { loading }] = useMutation<
    CreateTrainingRunJobMutation,
    CreateTrainingRunJobMutationVariables
  >(CREATE_TRAINING_RUN_JOB);

  const startTraining = useCallback(
    async (modelArchitectureId: string): Promise<StartTrainingResult> => {
      const shared = (skill.shared ?? {}) as SkillConfig;
      const taskId = typeof shared.taskId === 'string' ? shared.taskId : null;

      const projectId =
        typeof shared.projectId === 'string' ? shared.projectId : null;

      if (!taskId || !projectId) {
        return {
          success: false,
          error: 'Skill is missing taskId or projectId',
        };
      }

      try {
        const { data } = await mutate({
          variables: {
            input: {
              name: buildRunName(skill.name),
              trainingDataProjectId: projectId,
              modelArchitectureId,
              taskIds: [taskId],
              dockerImageTag: DEFAULT_DOCKER_IMAGE_TAG,
              platform: DEFAULT_PLATFORM,
              gpuCount: DEFAULT_GPU_COUNT,
              gpuType: DEFAULT_GPU_TYPE,
              containerDiskInGb: DEFAULT_CONTAINER_DISK_GB,
              maxHoursToTrain: DEFAULT_MAX_HOURS,
              hyperparameters: {},
            },
          },
          // Refetch the runs query so the new Pending row appears in the
          // TrainingStatusSection card immediately, which also flips
          // SkillDetailGeneralTab's `hasActive` gate → starts the poll loop.
          // Without this, the card stays in its pre-click state until the
          // user manually refreshes.
          refetchQueries: ['GetSkillTrainingRuns'],
        });

        const result = data?.createTrainingRunJob;

        return {
          success: Boolean(result?.success),
          error: result?.error ?? null,
        };
      } catch (e) {
        return {
          success: false,
          error: e instanceof Error ? e.message : String(e),
        };
      }
    },
    [mutate, skill.shared, skill.name],
  );

  return { startTraining, loading };
}
